import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { IconSetupLoader } from "../Svg";
import { useEffect, useState } from "react";
import { authApi, bookingApi } from "@api";
import { PATH_BOOKING, PATH_PAYMENT_FAIL } from "@routes";
import { ErrorToast } from "../toasts/ErrorToast";
import { AppModuleLayout } from "@layouts";
import { useDispatch } from "react-redux";
import { AppDispatch, initializeAccountDetail, loginV2, setJwt } from "@redux";
import { ssStorage, lsJwt, lsEmail, sessionHelper, ssCheckoutFlow, pushPurchase } from "@utilities";
import { breakpoint, theme } from "@utilities";
import { IAuthLogin } from "@interfaces";
import { ClipLoaderCommon } from "@components";
import { useOTPInput } from "src/hooks/useOTPInput";

export const SetupOrderCheckComponent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState(true);
    const [showThankYou, setShowThankYou] = useState(false);
    const [isGuestFlow, setIsGuestFlow] = useState(false);
    const [bookingRef, setBookingRef] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [otpVerified, setOtpVerified] = useState(false);
    const MAX_OTP_LENGTH = 5;

    const { handlePaste, handleInputChange } = useOTPInput(otpCode, setOtpCode);

    useEffect(() => {
        if (showThankYou && countdown > 0) {
            const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [showThankYou, countdown]);

    useEffect(() => {
        const tempJwt = ssStorage("tempJwt");
        const jwtData = tempJwt.getItem();

        if (jwtData) {
            lsJwt.setItem(jwtData);
            dispatch(setJwt(jwtData));
            tempJwt.removeItem();
            dispatch(initializeAccountDetail());
        }

        const completeBookingFailedMessage = `Booking check failed`;
        const completeBookingFailedParam = "?status=failed";

        const getPaymentErrorMessage = (search: string): string | null => {
            const urlParams = new URLSearchParams(search);
            const status = urlParams.get("status");
            if (status === "0") {
                const detailedMessage = urlParams.get("cmsgw_Message");
                const shortMessage = urlParams.get("msg");
                return detailedMessage || shortMessage || null;
            }
            return null;
        };

        const processOrder = async () => {
            try {
                const bookingReference = new URLSearchParams(location.search).get("bookingReference");

                const response = await bookingApi.completeOrder(location.search);

                const paymentErrorMessage = getPaymentErrorMessage(location.search);
                if (paymentErrorMessage) {
                    const checkoutFlow = ssCheckoutFlow.getItem();
                    navigate(PATH_PAYMENT_FAIL, {
                        state: { message: paymentErrorMessage, checkoutFlow },
                    });
                    return;
                }

                if (response.status === 200) {
                    const wasGuestCheckout = sessionStorage.getItem("guestCheckoutInProgress") === "true";

                    // GA4 purchase: emit once order is confirmed complete
                    try {
                        const gtmLastCheckout = ssStorage("gtmLastCheckout").getItem();
                        if (gtmLastCheckout?.product) {
                            pushPurchase(gtmLastCheckout.product, {
                                transactionId: bookingReference || "",
                                value: gtmLastCheckout.value,
                                travelDate: gtmLastCheckout.travelDate,
                            });
                        }
                        ssStorage("gtmLastCheckout").removeItem();
                    } catch (err) {
                        // Swallow GTM errors so they never break checkout flow
                        console.error("GTM purchase tracking failed:", err);
                    }

                    sessionHelper.clearBookNowSession();
                    ssCheckoutFlow.removeItem();
                    sessionStorage.removeItem("guestCheckoutInProgress");

                    const storedGuestEmail = sessionStorage.getItem("guestPrimaryEmail");
                    if (storedGuestEmail && !jwtData) {
                        try {
                            const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
                            let randomPassword = "";
                            for (let i = 0; i < 16; i++) {
                                randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
                            }
                            const signUpRes = await authApi.signUp({
                                email: storedGuestEmail,
                                password: randomPassword,
                                is_password_randomly_generated: true,
                            });
                            if (signUpRes.data.code === 200) {
                                const loginRes = (await dispatch(
                                    loginV2({ email: storedGuestEmail, password: randomPassword }),
                                )) as { payload: IAuthLogin };
                                if (loginRes.payload.data?.accessToken) {
                                    lsJwt.setItem(loginRes.payload.data);
                                    lsEmail.setItem(storedGuestEmail);
                                    dispatch(setJwt(loginRes.payload.data));
                                    await dispatch(initializeAccountDetail()).unwrap();
                                    authApi.resendVerificationOtp().catch(console.error);
                                }
                            }
                        } catch (err) {
                            console.error("Post-order guest account creation failed:", err);
                        }
                        sessionStorage.removeItem("guestPrimaryEmail");
                    }

                    const email = lsEmail.getItem() || storedGuestEmail || "";

                    setBookingRef(bookingReference || "");
                    setGuestEmail(email);
                    setIsGuestFlow(wasGuestCheckout);
                    setIsLoading(false);
                    setShowThankYou(true);
                    return;
                } else {
                    ErrorToast({ Message: completeBookingFailedMessage });
                    navigate(PATH_BOOKING + completeBookingFailedParam);
                }
            } catch (error) {
                console.error(error);
                const paymentErrorMessage = getPaymentErrorMessage(location.search);
                if (paymentErrorMessage) {
                    const checkoutFlow = ssCheckoutFlow.getItem();
                    navigate(PATH_PAYMENT_FAIL, {
                        state: { message: paymentErrorMessage, checkoutFlow },
                    });
                    return;
                }
                ErrorToast({ Message: completeBookingFailedMessage });
                navigate(PATH_BOOKING + completeBookingFailedParam);
            } finally {
                setIsLoading(false);
            }
        };
        processOrder();
    }, [location, dispatch, navigate]);

    const handleVerifyOtp = async () => {
        if (otpCode.length !== MAX_OTP_LENGTH) {
            ErrorToast({ Message: "Please enter a 5-digit code" });
            return;
        }
        setIsVerifying(true);
        try {
            const res = await authApi.verifyEmail({ otp: otpCode, email: guestEmail } as any);
            if (res.data.code === 200) {
                setOtpVerified(true);
                setTimeout(() => {
                    navigate(
                        PATH_BOOKING +
                            (bookingRef ? `?bookingReference=${bookingRef}` : ""),
                    );
                }, 1500);
            }
        } catch (error: any) {
            ErrorToast({ Message: error.response?.data?.title || "Invalid verification code. Please try again." });
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendOtp = async () => {
        if (isResending || countdown > 0) return;
        setIsResending(true);
        try {
            await authApi.resendVerificationOtp();
            setCountdown(60);
        } catch (err: any) {
            ErrorToast({ Message: err.response?.data?.title || "Failed to resend code" });
        } finally {
            setIsResending(false);
        }
    };

    const handleSkipToBookings = () => {
        navigate(
            PATH_BOOKING +
                (bookingRef ? `?bookingReference=${bookingRef}` : ""),
        );
    };

    if (showThankYou) {
        return (
            <AppModuleLayout>
                <StyledOuterWrapper>
                    <StyledThankYouWrapper>
                        <StyledLogoWrapper>
                            <img
                                src="/images/logos/Primary_logo_color.svg"
                                alt="logo"
                                className="logoItem2 logoColor"
                            />
                        </StyledLogoWrapper>

                        <ThankYouIcon>&#10003;</ThankYouIcon>
                        <ThankYouTitle>Thank You!</ThankYouTitle>
                        <ThankYouSubtitle>YOUR ADVENTURE IS LOCKED IN</ThankYouSubtitle>

                        {bookingRef && (
                            <BookingRefBox>
                                <BookingRefLabel>Your Booking Reference</BookingRefLabel>
                                <BookingRefValue>{bookingRef}</BookingRefValue>
                            </BookingRefBox>
                        )}

                        {isGuestFlow ? (
                            <>
                                {!otpVerified ? (
                                    <>
                                        <InfoText>
                                            We have sent you a 5 digit code to your email{guestEmail ? ` (${guestEmail})` : ""} to grab your vouchers.
                                            Please also take this opportunity to update your password.
                                        </InfoText>

                                        <OtpSection>
                                            <OtpLabel>Enter your 5-digit pin</OtpLabel>
                                            <OtpInput
                                                type="text"
                                                inputMode="numeric"
                                                pattern="\d*"
                                                maxLength={5}
                                                onChange={(e) => handleInputChange(e)}
                                                onPaste={handlePaste}
                                                value={otpCode || ""}
                                                placeholder="_ _ _ _ _"
                                                hideCaret={otpCode.length === MAX_OTP_LENGTH}
                                            />
                                            <VerifyButton
                                                onClick={handleVerifyOtp}
                                                disabled={otpCode.length !== MAX_OTP_LENGTH || isVerifying}
                                            >
                                                {isVerifying ? (
                                                    <ClipLoaderCommon color={theme.colors.highlight} size={24} margin={0} />
                                                ) : (
                                                    "Unlock My Tickets"
                                                )}
                                            </VerifyButton>

                                            {countdown > 0 ? (
                                                <ResendText>
                                                    Didn't receive a code? Resend in {countdown}s
                                                </ResendText>
                                            ) : (
                                                <ResendButton type="button" onClick={handleResendOtp} disabled={isResending}>
                                                    {isResending ? "Sending..." : "Resend Code"}
                                                </ResendButton>
                                            )}
                                        </OtpSection>

                                        <SkipLink onClick={handleSkipToBookings}>
                                            Skip for now - go to my bookings
                                        </SkipLink>
                                    </>
                                ) : (
                                    <VerifiedMessage>
                                        Email verified successfully! Redirecting to your booking...
                                    </VerifiedMessage>
                                )}
                            </>
                        ) : (
                            <>
                                <InfoText>
                                    Your tickets are ready and waiting for you.
                                </InfoText>
                                <VerifyButton onClick={handleSkipToBookings}>
                                    View My Tickets
                                </VerifyButton>
                            </>
                        )}
                    </StyledThankYouWrapper>
                </StyledOuterWrapper>
            </AppModuleLayout>
        );
    }

    return (
        <AppModuleLayout>
            <>
                {isLoading && (
                    <StyledOuterWrapper>
                        <StyledInnerWrapper>
                            <StyledLogoWrapper>
                                <img
                                    src="/images/logos/Primary_logo_color.svg"
                                    alt="logo"
                                    className="logoItem2 logoColor"
                                />
                            </StyledLogoWrapper>
                            <StyledCenteredContainer>
                                <IconSetupLoader />
                                <div>Verifying payment ...</div>
                            </StyledCenteredContainer>
                        </StyledInnerWrapper>
                    </StyledOuterWrapper>
                )}
            </>
        </AppModuleLayout>
    );
};

const StyledOuterWrapper = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
    padding: 80px 16px;
`;

const StyledInnerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 540px;
    background-color: #ffffff;
    padding: 64px 10px;
    border-radius: 4px;
`;

const StyledLogoWrapper = styled.div`
    height: 35px;
    margin-bottom: 30px;

    .logoItem2 {
        width: 260px;

        @media (min-width: 768px) {
            width: 300px;
        }

        @media (min-width: 1024px) {
            width: 350px;
        }
    }
`;

const StyledCenteredContainer = styled.div`
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: 60px;
`;

const StyledThankYouWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 540px;
    background-color: #ffffff;
    padding: 40px 30px;
    border-radius: 8px;
    text-align: center;

    ${breakpoint.breakOnlyMobile`
        width: 100%;
        padding: 30px 20px;
    `}
`;

const ThankYouIcon = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: white;
    font-size: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
`;

const ThankYouTitle = styled.h1`
    font-size: 28px;
    font-weight: 700;
    color: #000000;
    margin: 0 0 8px 0;
    font-family: "Poppins", sans-serif;

    ${breakpoint.breakOnlyMobile`
        font-size: 24px;
    `}
`;

const ThankYouSubtitle = styled.p`
    font-size: 16px;
    color: #555;
    margin: 0 0 24px 0;
    font-family: "Poppins", sans-serif;
`;

const BookingRefBox = styled.div`
    background: #f5f5f5;
    border-radius: 8px;
    padding: 16px 24px;
    margin-bottom: 24px;
    width: 100%;
`;

const BookingRefLabel = styled.div`
    font-size: 13px;
    color: #757575;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    font-family: "Poppins", sans-serif;
`;

const BookingRefValue = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: ${theme.colors.main};
    font-family: "Poppins", sans-serif;
    letter-spacing: 1px;
`;

const InfoText = styled.p`
    font-size: 14px;
    color: #555;
    line-height: 1.6;
    margin: 0 0 24px 0;
    font-family: "Poppins", sans-serif;
`;

const OtpSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
`;

const OtpLabel = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #333;
    font-family: "Poppins", sans-serif;
`;

const OtpInput = styled.input<{ hideCaret?: boolean }>`
    width: 200px;
    height: 50px;
    border-radius: 8px;
    border: 2px solid #e0e0e0;
    background: white;
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.6em;
    text-indent: 0.6em;
    color: #000000;
    transition: border-color 0.2s;
    caret-color: ${(props) => (props.hideCaret ? "transparent" : "auto")};

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }

    ${breakpoint.breakOnlyMobile`
        width: 180px;
        height: 48px;
        font-size: 20px;
    `}
`;

const VerifyButton = styled.button`
    width: 100%;
    max-width: 300px;
    height: 48px;
    border: none;
    border-radius: 4px;
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    font-family: "Poppins", sans-serif;
    transition: opacity 0.2s;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

const ResendText = styled.div`
    font-size: 13px;
    color: #757575;
    font-family: "Poppins", sans-serif;
`;

const ResendButton = styled.button`
    background: none;
    border: none;
    color: ${theme.colors.highlight};
    text-decoration: underline;
    font-size: 14px;
    cursor: pointer;
    padding: 0;
    font-weight: 500;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        opacity: 0.8;
    }
`;

const SkipLink = styled.button`
    background: none;
    border: none;
    color: #757575;
    font-size: 13px;
    cursor: pointer;
    text-decoration: underline;
    font-family: "Poppins", sans-serif;
    padding: 0;
    margin-top: 8px;

    &:hover {
        color: #333;
    }
`;

const VerifiedMessage = styled.div`
    font-size: 16px;
    color: ${theme.colors.main};
    font-weight: 600;
    margin-top: 16px;
    font-family: "Poppins", sans-serif;
`;
